// Builder 2 — Home archetype display polish.
//
// The Style Archetype on Home must look like a luxury product identity,
// never a raw backend enum. Handles snake_case / kebab-case / camelCase
// and provides a graceful fallback for missing / unknown values.
//
// formatArchetypeTitle('minimalist_sage')   -> 'The Minimalist Sage'
// formatArchetypeTitle('creative-rebel')    -> 'The Creative Rebel'
// formatArchetypeTitle('classicRuler')      -> 'The Classic Ruler'
// formatArchetypeTitle('The Dramatic Rebel')-> 'The Dramatic Rebel' (idempotent)
// formatArchetypeTitle('')                  -> 'Your Style Genome is forming'
// formatArchetypeTitle(null)                -> 'Your Style Genome is forming'
// formatArchetypeTitle('unknown_archetype') -> 'Your Style Genome is forming'

const STOPWORDS = new Set([
  'unknown', 'unknown_archetype', 'undefined', 'null',
  'style_genome_primary', 'style-genome-primary',
]);

// Short editorial descriptions for the founder-approved archetypes.
// Missing keys fall back to a neutral line.
const SUBTITLES = {
  minimalist_sage: 'Quiet structure, refined ease, and intentional detail.',
  dramatic_rebel: 'Bold silhouettes, sharp contrast, considered edge.',
  classic_ruler: 'Timeless tailoring with unmistakable authority.',
  creative_rebel: 'Playful proportions, curated risk, editorial mix.',
  romantic_muse: 'Softness with structure. Details that reward a second look.',
  urban_nomad: 'Utility refined into a signature. Ready for anywhere.',
  quiet_luxury: 'Understatement, elevated. The confidence of restraint.',
  editorial_maverick: 'Runway-borrowed instincts, tuned for real life.',
};

const _canonicalise = (raw) => {
  if (raw === null || raw === undefined) return '';
  const s = String(raw).trim();
  if (!s) return '';
  // Normalise separators + camelCase → snake_case for lookup.
  return s
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .replace(/-/g, '_')
    .toLowerCase();
};

const _titleCaseWord = (w) => (w ? w[0].toUpperCase() + w.slice(1) : w);

export const formatArchetypeTitle = (value) => {
  const key = _canonicalise(value);
  if (!key) return 'Your Style Genome is forming';
  if (STOPWORDS.has(key)) return 'Your Style Genome is forming';

  const words = key.split(/[_\s]+/).filter(Boolean).map(_titleCaseWord);
  if (words.length === 0) return 'Your Style Genome is forming';

  const already_has_leading_the = /^the\s+/i.test(String(value).trim());
  const title = words.join(' ');
  return already_has_leading_the ? `The ${title.replace(/^The\s+/i, '')}` : `The ${title}`;
};

export const formatArchetypeSubtitle = (value) => {
  const key = _canonicalise(value);
  if (!key || STOPWORDS.has(key)) {
    return 'Take a few quick prompts and we\u2019ll shape your archetype.';
  }
  return SUBTITLES[key] || 'A shape of style that\u2019s uniquely yours.';
};

// Convenience: given a profile object, prefer human-readable name fields
// but fall back to the canonical key. Never returns a snake_case string.
export const formatArchetypeFromProfile = (profile) => {
  if (!profile) return formatArchetypeTitle(null);
  // If backend already returns a display name, still normalise so it can't
  // sneak through as snake_case.
  const raw = profile.archetype_display_name
    || profile.archetype_name
    || profile.archetype_key
    || profile.archetype
    || null;
  return formatArchetypeTitle(raw);
};

export const subtitleFromProfile = (profile) => {
  if (!profile) return formatArchetypeSubtitle(null);
  const raw = profile.archetype_key || profile.archetype_name || profile.archetype || null;
  return formatArchetypeSubtitle(raw);
};
